import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, type UploadInfo } from "../../api";

/* =============================================================================
 * MEDIA PICKER — Bilder/Videos für Phone-Mockups & Co.
 * -----------------------------------------------------------------------------
 * Two modes:
 *   - mode="single":  edits a single URL (string | undefined)
 *   - mode="list":    edits an ordered array of URLs (string[])
 *
 * Features:
 *   - Drag & Drop a file onto the picker → uploads + selects.
 *   - "Hochladen"-Button → file input (multiple).
 *   - "Library"-Button → modal with all previously uploaded files.
 *   - "URL einfügen" → manual text input (e.g. external URL).
 *   - Per-slot preview (thumbnail for images, ▶ poster for videos).
 *   - Per-slot remove + delete-from-server (icon trash on hover).
 * =========================================================================== */

type SingleProps = {
  mode: "single";
  value?: string;
  onChange: (next: string | undefined) => void;
  label?: string;
  hint?: string;
  /** Limit accepted MIME types: "image" | "video" | "any" (default). */
  accept?: "image" | "video" | "any";
};

type ListProps = {
  mode: "list";
  value: string[];
  onChange: (next: string[]) => void;
  /** How many slots to show. If `count` is fixed, slot count = count.
   *  If `count` is "auto", we show value.length + 1 (one always-empty add). */
  count: number | "auto";
  /** Optional per-slot label (e.g. "Screen 1"). */
  slotLabel?: (i: number) => string;
  label?: string;
  hint?: string;
  accept?: "image" | "video" | "any";
};

type Props = SingleProps | ListProps;

const acceptToAttr = (a: Props["accept"]): string => {
  if (a === "image") return "image/*";
  if (a === "video") return "video/*";
  return "image/*,video/*";
};

const isVideoUrl = (url: string): boolean => /\.(mp4|webm|mov|m4v)(\?.*)?$/i.test(url);

/** Renders a thumbnail-sized preview for an image OR video URL. */
const MediaThumb: React.FC<{ url: string; large?: boolean }> = ({ url, large }) => {
  if (!url) return <div className="media-thumb empty">∅</div>;
  if (isVideoUrl(url)) {
    return (
      <div className={`media-thumb video ${large ? "large" : ""}`}>
        <video src={url} muted preload="metadata" />
        <span className="media-thumb-play">▶</span>
      </div>
    );
  }
  return (
    <div className={`media-thumb image ${large ? "large" : ""}`}>
      <img src={url} alt="" />
    </div>
  );
};

/* ------------------------------------------------------------ *
 *  LIBRARY MODAL — pick from previously uploaded files.        *
 * ------------------------------------------------------------ */
const LibraryModal: React.FC<{
  accept: Props["accept"];
  onPick: (url: string) => void;
  onClose: () => void;
}> = ({ accept, onPick, onClose }) => {
  const [items, setItems] = useState<UploadInfo[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const refresh = useCallback(async () => {
    try {
      const r = await api.uploadsList();
      setItems(r.items);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const onUpload = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;
      setBusy(true);
      setErr(null);
      try {
        const r = await api.uploadFiles(files);
        if (r.uploaded[0]) onPick(r.uploaded[0].url);
        await refresh();
      } catch (e) {
        setErr((e as Error).message);
      } finally {
        setBusy(false);
      }
    },
    [onPick, refresh],
  );

  const onDelete = useCallback(
    async (filename: string) => {
      if (!confirm(`„${filename}" wirklich löschen?`)) return;
      try {
        await api.uploadsDelete(filename);
        await refresh();
      } catch (e) {
        setErr((e as Error).message);
      }
    },
    [refresh],
  );

  const filtered: UploadInfo[] | null = items
    ? items.filter((it) => {
        if (accept === "image") return it.kind === "image";
        if (accept === "video") return it.kind === "video";
        return true;
      })
    : null;

  return (
    <div className="media-lib-backdrop" onMouseDown={onClose}>
      <div
        className="media-lib"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Media-Library"
      >
        <div className="media-lib-head">
          <div className="media-lib-title">📚 Media-Library</div>
          <button className="btn ghost sm" onClick={onClose} aria-label="Schließen">
            ✕
          </button>
        </div>
        <div className="media-lib-tools">
          <button
            className="btn primary sm"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
          >
            {busy ? "⏳ Lade hoch…" : "⬆ Datei hochladen"}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept={acceptToAttr(accept)}
            multiple
            style={{ display: "none" }}
            onChange={(e) => {
              void onUpload(e.target.files);
              e.target.value = "";
            }}
          />
          <span className="media-lib-hint">
            oder Datei in den Bereich unten ziehen
          </span>
        </div>
        {err && <div className="media-lib-err">⚠ {err}</div>}

        <div
          className="media-lib-grid"
          onDragOver={(e) => {
            e.preventDefault();
          }}
          onDrop={(e) => {
            e.preventDefault();
            void onUpload(e.dataTransfer.files);
          }}
        >
          {filtered === null ? (
            <div className="media-lib-empty">Lade…</div>
          ) : filtered.length === 0 ? (
            <div className="media-lib-empty">
              Keine Uploads — ziehe Bilder/Videos in dieses Fenster oder klicke
              auf „Datei hochladen".
            </div>
          ) : (
            filtered.map((it) => (
              <div key={it.filename} className="media-lib-card">
                <button
                  type="button"
                  className="media-lib-card-pick"
                  onClick={() => onPick(it.url)}
                  title="Auswählen"
                >
                  <MediaThumb url={it.url} />
                  <div className="media-lib-card-meta">
                    <div className="media-lib-card-name">{it.filename}</div>
                    <div className="media-lib-card-sub">
                      {it.kind} · {(it.size / 1024).toFixed(0)} KB
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  className="media-lib-card-del"
                  onClick={() => void onDelete(it.filename)}
                  title="Löschen"
                  aria-label="Löschen"
                >
                  🗑
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------ *
 *  SLOT — single URL editor with thumbnail + 3 actions          *
 * ------------------------------------------------------------ */
const MediaSlot: React.FC<{
  value?: string;
  onChange: (next: string | undefined) => void;
  accept: Props["accept"];
  label?: string;
}> = ({ value, onChange, accept, label }) => {
  const [showLib, setShowLib] = useState(false);
  const [showUrl, setShowUrl] = useState(false);
  const [urlDraft, setUrlDraft] = useState("");
  const [dropActive, setDropActive] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const onUpload = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;
      setBusy(true);
      try {
        const r = await api.uploadFiles(files);
        if (r.uploaded[0]) onChange(r.uploaded[0].url);
      } catch (e) {
        alert(`Upload fehlgeschlagen: ${(e as Error).message}`);
      } finally {
        setBusy(false);
      }
    },
    [onChange],
  );

  return (
    <div
      className={`media-slot ${value ? "has-value" : "empty"} ${dropActive ? "drop-active" : ""}`}
      onDragOver={(e) => {
        e.preventDefault();
        setDropActive(true);
      }}
      onDragLeave={() => setDropActive(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDropActive(false);
        void onUpload(e.dataTransfer.files);
      }}
    >
      {label && <div className="media-slot-label">{label}</div>}
      <div className="media-slot-body">
        {value ? <MediaThumb url={value} large /> : (
          <div className="media-slot-placeholder">
            <div className="media-slot-placeholder-icon">🖼</div>
            <div className="media-slot-placeholder-hint">Datei hierher ziehen</div>
          </div>
        )}
      </div>
      <div className="media-slot-actions">
        <button
          type="button"
          className="btn primary sm"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
        >
          {busy ? "⏳" : "⬆"} {value ? "Ersetzen" : "Hochladen"}
        </button>
        <button type="button" className="btn ghost sm" onClick={() => setShowLib(true)}>
          📚 Library
        </button>
        <button
          type="button"
          className="btn ghost sm"
          onClick={() => {
            setUrlDraft(value ?? "");
            setShowUrl((v) => !v);
          }}
        >
          🔗 URL
        </button>
        {value && (
          <button
            type="button"
            className="btn ghost sm danger"
            onClick={() => onChange(undefined)}
            title="Entfernen (nur aus Scene, Datei bleibt)"
          >
            ✕
          </button>
        )}
      </div>
      {showUrl && (
        <div className="media-slot-url">
          <input
            type="text"
            value={urlDraft}
            placeholder="https://… oder /static/uploads/foo.mp4"
            onChange={(e) => setUrlDraft(e.target.value)}
            autoFocus
          />
          <button
            type="button"
            className="btn primary sm"
            onClick={() => {
              onChange(urlDraft.trim() || undefined);
              setShowUrl(false);
            }}
          >
            Übernehmen
          </button>
        </div>
      )}
      <input
        ref={fileRef}
        type="file"
        accept={acceptToAttr(accept)}
        style={{ display: "none" }}
        onChange={(e) => {
          void onUpload(e.target.files);
          e.target.value = "";
        }}
      />
      {showLib && (
        <LibraryModal
          accept={accept}
          onPick={(url) => {
            onChange(url);
            setShowLib(false);
          }}
          onClose={() => setShowLib(false)}
        />
      )}
    </div>
  );
};

/* ------------------------------------------------------------ *
 *  PUBLIC COMPONENT                                              *
 * ------------------------------------------------------------ */
export const MediaPicker: React.FC<Props> = (props) => {
  if (props.mode === "single") {
    return (
      <div className="media-picker">
        {props.label && <div className="media-picker-label">{props.label}</div>}
        {props.hint && <div className="media-picker-hint">{props.hint}</div>}
        <MediaSlot
          value={props.value}
          onChange={props.onChange}
          accept={props.accept}
          label={undefined}
        />
      </div>
    );
  }

  // list mode
  const slots = props.count === "auto"
    ? Math.max(1, props.value.length + 1)
    : Math.max(1, props.count);
  const setAt = (i: number, next: string | undefined): void => {
    const arr = [...props.value];
    while (arr.length <= i) arr.push("");
    arr[i] = next ?? "";
    // Trim trailing empty entries to keep state lean.
    while (arr.length && arr[arr.length - 1] === "") arr.pop();
    props.onChange(arr);
  };
  return (
    <div className="media-picker list">
      {props.label && <div className="media-picker-label">{props.label}</div>}
      {props.hint && <div className="media-picker-hint">{props.hint}</div>}
      <div className="media-picker-list">
        {Array.from({ length: slots }).map((_, i) => (
          <MediaSlot
            key={i}
            value={props.value[i] || undefined}
            onChange={(next) => setAt(i, next)}
            accept={props.accept}
            label={props.slotLabel ? props.slotLabel(i) : `Screen ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
};
